import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrainCircuit, Target, Plus, Minus } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';

const faqs = [
  {
    question: "What exactly is Pabs?",
    answer: "Pabs is the Product Authority layer for teams building with AI. It combines the product management work that decides what should be built with the Design Authority work that keeps concepts and builds aligned with agreed standards."
  },
  {
    question: "Who is Pabs built for?",
    answer: "Pabs is built for founders and teams using AI to turn ideas into working software. It also fits enterprise AI workspaces where more people can prototype, but product intent, design standards, and human decision rights still need to remain consistent."
  },
  {
    question: "How is Pabs different from Notion, Jira, or Confluence?",
    answer: "Those tools coordinate work: tickets, sprints, documentation, deadlines, and delivery. Pabs governs the intent behind that work and reviews whether the output still matches it. Keep using Linear, Jira, Notion, or Asana. Pabs connects product direction and design standards to the tools and agents doing the work."
  },
  {
    question: "What is MCP and why does it matter?",
    answer: "MCP (Model Context Protocol) is an open standard that lets AI tools like Claude and Cursor use structured context and capabilities from external systems. Pabs uses MCP to bring current strategy, decisions, design standards, and governed jobs into the AI workspace."
  },
  {
    question: "Does Pabs use my data to train AI models?",
    answer: "No. We never use your strategic plans, briefs, decisions, or any personal data to train AI models. Your strategy is yours. It's encrypted at rest, only accessible by you, and never shared with third parties."
  },
  {
    question: "Is Pabs free?",
    answer: "Pabs has a free tier. Use top-ups when you need a specific product or Design Authority job done, or keep Pabs always on when decisions and reviews arrive continuously. You pay for the work Pabs performs, not for another project dashboard."
  },
  {
    question: "Does the quality of my brief affect the output?",
    answer: "Absolutely. The sharper your brief, the more precise and relevant Pabs' strategy becomes, just like briefing a great designer or strategist. Clear context gives Pabs what it needs to generate plans that genuinely align with your vision."
  },
  {
    question: "Was Pabs built by a big team?",
    answer: "No developers were involved in building Pabs. It was an experiment to see what one person could ship with AI from start to finish. That's exactly why we believe in what we're building: Pabs is proof that ambitious products can be built without a full team, when strategy and AI are properly connected."
  },
  {
    question: "What's next for Pabs?",
    answer: "We're focused on deepening the MCP layer, expanding Product and Design Authority jobs, and connecting more enterprise knowledge and delivery systems. The goal is a governed product context that learns as the product changes and stays available wherever people and agents build."
  },
];

function FAQItem({ question, answer, index }) {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="border-b border-brand-800"
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-6 py-6 text-left group"
      >
        <span className={`text-base md:text-lg font-semibold leading-snug transition-colors duration-200 ${open ? 'text-brand-50' : 'text-brand-300 group-hover:text-brand-50'}`}>
          {question}
        </span>
        <span className={`shrink-0 w-7 h-7 rounded-full border flex items-center justify-center transition-all duration-200 ${open ? 'bg-brand-50 border-brand-50 text-brand-900' : 'border-brand-700 text-brand-500 group-hover:border-brand-400 group-hover:text-brand-300'}`}>
          {open ? <Minus className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="text-brand-400 text-sm md:text-base leading-relaxed pb-6 max-w-3xl">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-brand-900 flex flex-col font-sans text-brand-50">
      <Header />

      <main className="flex-grow">
        {/* Hero / Statement */}
        <section className="relative overflow-hidden bg-brand-900 text-brand-50">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:80px_80px]"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-900/50 to-brand-900"></div>
          </div>
          <div className="container mx-auto px-4 relative z-10 pt-40 pb-24">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-8xl lg:text-[100px] font-medium leading-[0.95] mb-12">
                Building got easier.<br />
                <span className="text-brand-500">Product judgment did not.</span>
              </h1>

              <div className="text-xl md:text-2xl text-brand-400 font-light leading-relaxed space-y-8 border-l border-brand-800 pl-8">
                <p>
                  <strong className="text-brand-50 font-bold">Execution is faster with AI.</strong> But speed does not tell you whether the thing you are building has a market, a customer, or a reason to exist.
                </p>
                <p>
                  Teams gained an AI workforce. They did not automatically gain the product and design authority that decides what should be built, defines the boundaries, and reviews what comes back. Pabs changes that.
                </p>
              </div>
            </motion.div>
          </div>
          </div>
        </section>

        {/* The Problem / Solution Split */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">

            {/* Left: The Trap */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-sm font-bold text-brand-500 uppercase tracking-widest mb-6">The Efficiency Trap</h3>
              <h2 className="text-3xl md:text-4xl font-medium mb-6 text-brand-300">
                Faster shipping creates<br/>new risk.
              </h2>
              <p className="text-brand-400 leading-relaxed mb-6">
                When building is instant, founders can go deeper on ideas that never had a real
                path to customers, revenue, or a defensible business.
              </p>
              <ul className="space-y-4 text-brand-400">
                <li className="flex items-start gap-3">
                  <span className="text-brand-600 font-mono">01</span>
                  <span>Zero validation before code is written.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand-600 font-mono">02</span>
                  <span>"Vibe coding" leads to untraceable spaghetti logic.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-brand-600 font-mono">03</span>
                  <span>Business risk hides inside shipping momentum.</span>
                </li>
              </ul>
            </motion.div>

            {/* Right: The Pabs Way */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-sm font-bold text-brand-50 uppercase tracking-widest mb-6 flex items-center gap-2">
                <span className="w-2 h-2 bg-brand-50 rounded-full animate-pulse"></span>
                The Pabs Method
              </h3>
              <h2 className="text-3xl md:text-4xl font-medium mb-6 text-brand-50">
                Product and Design Authority, always on.
              </h2>
              <p className="text-brand-400 leading-relaxed mb-6">
                Pabs keeps product intent alive through the build. It defines the problem, challenges
                assumptions, prioritises the next move, gives agents the right context, and reviews
                concepts and builds against the standards your team agreed to.
              </p>

              <div className="grid grid-cols-1 gap-6 pt-4">
                <div className="group flex items-start gap-4">
                  <BrainCircuit className="w-6 h-6 text-brand-300 shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-brand-50 text-lg">Intent survives execution.</h4>
                    <p className="text-sm text-brand-500">Pabs keeps strategy, evidence, design standards, and build direction connected as people and agents work.</p>
                  </div>
                </div>
                <div className="group flex items-start gap-4">
                  <Target className="w-6 h-6 text-brand-300 shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-brand-50 text-lg">The boundary stays clear.</h4>
                    <p className="text-sm text-brand-500">Pabs does not replace your AI clients, delivery tools, or design system. It gives them direction, boundaries, review, and a path back to human judgment.</p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </section>

        {/* Minimal Feature List */}
        <section className="px-6 md:px-12 border-t border-brand-800 pt-20 mb-32">
          <div className="container mx-auto max-w-4xl text-center">
            <h3 className="text-2xl font-medium text-brand-50 mb-10">
              One authority layer for every product decision and build
            </h3>

            <div className="flex flex-wrap gap-3">
              {[
                'Product Problem Definition',
                'Assumption Challenge',
                'Product Prioritisation',
                'Success Measures',
                'Competitive Context',
                'Product Pulse',
                'MCP Protocol Native',
                'Decision Traceability',
                'Continuous Product Learning',
                'Design Authority Reviews',
                'Build-to-Design Checks'
              ].map((tag, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 bg-brand-800/50 text-brand-300 border border-brand-700/50 text-sm font-medium rounded-full hover:bg-brand-800 hover:text-brand-50 transition-colors cursor-default"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-6 md:px-12 border-t border-brand-800 pt-20">
          <div className="container mx-auto max-w-4xl">
            <div className="mb-16">
              <p className="text-sm font-bold text-brand-500 uppercase tracking-widest mb-4">FAQ</p>
              <h2 className="text-3xl md:text-4xl font-medium text-brand-50 leading-tight mb-4">
                Frequently asked.
              </h2>
              <p className="text-brand-400 text-lg font-light leading-relaxed">
                Everything you need to know about Pabs, from how it works to what's coming next.
              </p>
            </div>

            <div className="border-t border-brand-800">
              {faqs.map((faq, idx) => (
                <FAQItem key={idx} index={idx} question={faq.question} answer={faq.answer} />
              ))}
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
